import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Bot, 
  Heart, 
  AlertTriangle, 
  Pill, 
  Activity, 
  Utensils, 
  Smile, 
  Brain,
  BarChart3,
  Clock,
  TrendingUp
} from 'lucide-react';

const AgentDetailsModal = ({ 
  isOpen, 
  onClose, 
  currentAgent, 
  agentStats, 
  agentHistory,
  availableAgents 
}) => {
  
  const getAgentIcon = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return <Heart className="w-5 h-5" />;
      case 'EmergencyAgent':
        return <AlertTriangle className="w-5 h-5" />;
      case 'MedicationAgent':
        return <Pill className="w-5 h-5" />;
      case 'VitalsMonitoringAgent':
        return <Activity className="w-5 h-5" />;
      case 'NutritionAgent':
        return <Utensils className="w-5 h-5" />;
      case 'MentalHealthAgent':
        return <Smile className="w-5 h-5" />;
      case 'HealthResearchAgent':
        return <Brain className="w-5 h-5" />;
      default:
        return <Bot className="w-5 h-5" />;
    }
  };

  const getAgentDescription = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return 'Sophira is your primary health companion, providing general guidance and routing to specialized agents when needed.';
      case 'EmergencyAgent':
        return 'Specialized in emergency situations, providing immediate first aid guidance and connecting you with emergency services.';
      case 'MedicationAgent':
        return 'Expert in medication management, drug interactions, dosage guidelines, and adherence strategies.';
      case 'VitalsMonitoringAgent':
        return 'Focuses on vital signs interpretation, trend analysis, and monitoring guidance for optimal health tracking.';
      case 'NutritionAgent':
        return 'Provides nutrition guidance for elderly health, diabetic management, and heart-healthy diet recommendations.';
      case 'MentalHealthAgent':
        return 'Offers emotional support, mood tracking assistance, and mental wellness strategies for elderly patients.';
      case 'HealthResearchAgent':
        return 'Conducts comprehensive health research using the latest medical information and evidence-based sources.';
      default:
        return 'AI health assistant providing specialized healthcare support.';
    }
  };

  const getAgentColor = (agentName) => {
    switch (agentName) {
      case 'MainHealthAgent':
        return 'bg-blue-500';
      case 'EmergencyAgent':
        return 'bg-red-500';
      case 'MedicationAgent':
        return 'bg-purple-500';
      case 'VitalsMonitoringAgent':
        return 'bg-green-500';
      case 'NutritionAgent':
        return 'bg-orange-500';
      case 'MentalHealthAgent':
        return 'bg-pink-500';
      case 'HealthResearchAgent':
        return 'bg-indigo-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className={`p-3 rounded-full ${getAgentColor(currentAgent)} text-white`}>
                  {getAgentIcon(currentAgent)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    Agent Information
                  </h2>
                  <p className="text-sm text-gray-500">
                    Current: {currentAgent.replace('Agent', '')}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Current Agent Details */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Bot className="w-5 h-5 mr-2" />
                  Current Agent
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-lg ${getAgentColor(currentAgent)} text-white`}>
                      {getAgentIcon(currentAgent)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800 mb-1">
                        {currentAgent.replace('Agent', '')}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {getAgentDescription(currentAgent)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agent Statistics */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Session Statistics
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-blue-600">Total Messages</span>
                      <span className="text-2xl font-bold text-blue-700">
                        {agentStats?.totalMessages || 0}
                      </span>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-green-600">Active Agents</span>
                      <span className="text-2xl font-bold text-green-700">
                        {agentStats?.agentUsage ? Object.keys(agentStats.agentUsage).length : 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Agent Usage Breakdown */}
              {agentStats?.agentUsage && Object.keys(agentStats.agentUsage).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Agent Usage
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(agentStats.agentUsage).map(([agent, count]) => (
                      <div key={agent} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <div className={`p-1 rounded ${getAgentColor(agent)} text-white`}>
                            {getAgentIcon(agent)}
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {agent.replace('Agent', '')}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-600">
                          {count} messages
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Last Activity */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Last Activity
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    {formatTimestamp(agentStats?.lastActivity)}
                  </p>
                </div>
              </div>

              {/* Available Agents */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Available Agents
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  {availableAgents?.map((agent) => (
                    <div 
                      key={agent.type} 
                      className={`p-3 rounded-lg border-2 transition-colors ${
                        agent.name === currentAgent 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded ${getAgentColor(agent.name)} text-white`}>
                          {getAgentIcon(agent.name)}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-800">
                            {agent.name.replace('Agent', '')}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {agent.description}
                          </p>
                        </div>
                        {agent.name === currentAgent && (
                          <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                            Active
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AgentDetailsModal; 